import { Link } from "react-router-dom";

/**
 * BrandMark v2 — Côte Perdue's refined signature monogram.
 * Double hand-drawn circle, serif ornaments (stars, laurels-light), a gothic
 * "EST MMXXVI" engraving arc and the CP monogram. Heraldic chic, not clichéd.
 */
export default function BrandMark({
    size = 56,
    color = "currentColor",
    tagline = false,
    link = false,
    className = "",
    testid = "brand-mark",
    variant = "default", // 'default' | 'seal' (no tagline, bigger ornament)
}) {
    const inner = (
        <span
            className={`cp-brandmark inline-flex items-center gap-4 ${className}`}
            data-testid={testid}
            style={{ color }}
        >
            <svg
                width={size}
                height={size}
                viewBox="0 0 140 140"
                fill="none"
                aria-label="Côte Perdue"
                className="shrink-0"
            >
                {/* Outer hand-drawn circle */}
                <path
                    d="M 70 6 C 104 7 127 30 132 65 C 133 101 104 130 70 132 C 34 132 7 104 7 68 C 7 32 34 8 70 6 Z"
                    stroke={color} strokeWidth="0.9" fill="none" opacity="0.85"
                />
                {/* Inner thin circle */}
                <circle cx="70" cy="70" r="55" stroke={color} strokeWidth="0.35" fill="none" opacity="0.5" />

                {/* EST · MMXXVI — curved engraving at the bottom */}
                <defs>
                    <path id="cp-arc" d="M 22 84 A 52 52 0 0 0 118 84" />
                </defs>
                <text
                    fill={color}
                    fontFamily="'Cormorant Garamond', serif"
                    fontSize="9"
                    letterSpacing="3"
                    opacity="0.9"
                    fontWeight="400"
                >
                    <textPath href="#cp-arc" startOffset="50%" textAnchor="middle">
                        EST · MMXXVI
                    </textPath>
                </text>

                {/* Top decorative serif-star */}
                <g transform="translate(70 14)">
                    <path d="M 0 -5 L 1.2 -1.2 L 5 0 L 1.2 1.2 L 0 5 L -1.2 1.2 L -5 0 L -1.2 -1.2 Z"
                          fill={color} opacity="0.85" />
                </g>

                {/* Small side dots */}
                <circle cx="14" cy="70" r="1.1" fill={color} opacity="0.7" />
                <circle cx="126" cy="70" r="1.1" fill={color} opacity="0.7" />

                {/* Decorative serif flourishes (left & right brackets) */}
                <path d="M 20 52 Q 14 58 20 68" stroke={color} strokeWidth="0.5" fill="none" opacity="0.5" />
                <path d="M 120 52 Q 126 58 120 68" stroke={color} strokeWidth="0.5" fill="none" opacity="0.5" />

                {/* C — serif italic, etched */}
                <text
                    x="38" y="80"
                    fontFamily="'Cormorant Garamond', serif"
                    fontStyle="italic" fontWeight="500" fontSize="62"
                    fill={color} letterSpacing="-1"
                >C</text>

                {/* P — interlocking with the C */}
                <text
                    x="62" y="88"
                    fontFamily="'Cormorant Garamond', serif"
                    fontStyle="italic" fontWeight="500" fontSize="62"
                    fill={color} letterSpacing="-1"
                >P</text>
            </svg>

            {tagline && (
                <span className="flex flex-col leading-none whitespace-nowrap">
                    <span
                        className="cp-serif text-[20px] md:text-[22px] xl:text-[26px] leading-none tracking-tight"
                        style={{ color }}
                    >
                        Côte <em className="cp-display-italic">Perdue</em>
                    </span>
                    <span
                        className="cp-tiny mt-1.5 opacity-70 hidden md:inline-block"
                        style={{ color, fontSize: "9px", letterSpacing: "0.24em" }}
                    >
                        La côte qu'on se cache
                    </span>
                </span>
            )}
        </span>
    );

    if (link) return <Link to="/" className="group" data-testid={`${testid}-link`}>{inner}</Link>;
    return inner;
}
